import {
  BarChart3,
  BookOpen,
  ClipboardList,
  FileText,
  Home,
  Image as ImageIcon,
  LayoutDashboard,
  LineChart,
  Layers,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type NavItem = {
  label: string;
  /** Short label for the mobile bottom bar, where space is tight. */
  short: string;
  icon: LucideIcon;
  path: string;
};

/**
 * Primary navigation, per role.
 *
 * Each role gets its own list rather than one shared list with items hidden or
 * redirected — staff and students have genuinely different jobs, and the old
 * single list meant a content author navigated a student's app.
 *
 * Every path here must be a real route in App.tsx that the role's guard admits;
 * a nav item the role would 403 on is worse than no nav item. Lists are capped
 * at five so the mobile bottom bar stays legible.
 */
export const ROLE_NAV: Record<string, NavItem[]> = {
  student: [
    { label: 'Home', short: 'Home', icon: Home, path: '/' },
    { label: 'Subjects', short: 'Subjects', icon: BookOpen, path: '/subjects' },
    { label: 'AI Workspace', short: 'AI', icon: Sparkles, path: '/ai' },
    { label: 'Tests', short: 'Tests', icon: ClipboardList, path: '/assessment' },
    { label: 'Dashboard', short: 'Dashboard', icon: BarChart3, path: '/dashboard' },
  ],

  // Faculty review and approve content, and browse the same Structure CMS
  // that admin/content authors update.
  faculty: [
    { label: 'Dashboard', short: 'Dashboard', icon: LayoutDashboard, path: '/faculty' },
    { label: 'Structure', short: 'Structure', icon: Layers, path: '/cms/structure' },
    { label: 'Review Queue', short: 'Review', icon: ShieldCheck, path: '/cms/review' },
    { label: 'Question Bank', short: 'Questions', icon: FileText, path: '/cms/questions' },
    { label: 'Insights', short: 'Insights', icon: LineChart, path: '/cms/insights' },
  ],

  // Content authors live in the CMS.
  content: [
    { label: 'Dashboard', short: 'Dashboard', icon: LayoutDashboard, path: '/content' },
    { label: 'Structure', short: 'Structure', icon: Layers, path: '/cms/structure' },
    { label: 'Question Bank', short: 'Questions', icon: FileText, path: '/cms/questions' },
    { label: 'Media', short: 'Media', icon: ImageIcon, path: '/cms/media' },
    { label: 'Insights', short: 'Insights', icon: LineChart, path: '/cms/insights' },
  ],

  // Admins run the platform. Users/business/reports are tabs inside the admin
  // console (local state, not routes), so they aren't linkable from here yet.
  admin: [
    { label: 'Dashboard', short: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { label: 'CMS', short: 'CMS', icon: Layers, path: '/cms' },
    { label: 'Question Bank', short: 'Questions', icon: FileText, path: '/cms/questions' },
    { label: 'Review', short: 'Review', icon: ShieldCheck, path: '/cms/review' },
  ],
};

/** Nav for a role, falling back to the student list for guests and unknown roles. */
export function navForRole(role?: string | null): NavItem[] {
  return ROLE_NAV[role ?? ''] ?? ROLE_NAV.student;
}
